import config from '../config';
import { convertUrlsToRelative, convertUrlsToAbsolute } from '../utils/urlUtils';

// Configuration API
const API_CONFIG = {
    baseUrl: config.apiUrl,
    enabled: true // Mettre à false pour désactiver l'API
};

// Helper pour obtenir les headers avec JWT
function getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
    };
}

// Helper pour gérer les erreurs d'authentification
function handleAuthError(response) {
    if (response.status === 401) {
        // Token expiré ou invalide - soft logout
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        throw new Error('Session expirée. Veuillez vous reconnecter.');
    }
}

// Service API
const API = {
    // Sauvegarder une signature (legacy - utilise encore les fichiers JSON)
    async saveSignature(data) {
        if (!API_CONFIG.enabled) return null;

        try {
            // Convertir les URLs absolues en chemins relatifs avant sauvegarde
            const dataToSave = {
                ...data,
                grid_data: data.grid_data ? convertUrlsToRelative(data.grid_data) : data.grid_data
            };

            const response = await fetch(`${API_CONFIG.baseUrl}/signature/save`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(dataToSave)
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la sauvegarde');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Charger une signature
    async loadSignature(uid) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/signature/${uid}`);
            if (!response.ok) throw new Error('Signature non trouvée');
            const result = await response.json();

            // Convertir les chemins relatifs en URLs absolues
            if (result.grid_data) {
                result.grid_data = convertUrlsToAbsolute(result.grid_data);
            }

            return result;
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Uploader une image
    async uploadImage(file) {
        if (!API_CONFIG.enabled) return null;

        try {
            const formData = new FormData();
            formData.append('file', file);

            const token = localStorage.getItem('access_token');
            const response = await fetch(`${API_CONFIG.baseUrl}/upload`, {
                method: 'POST',
                headers: {
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: formData
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de l\'upload');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Nouvelles méthodes avec authentification JWT

    // Lister les signatures de l'utilisateur
    async getSignatures() {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/signatures`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors du chargement');
            }
            const result = await response.json();

            // Convertir les chemins relatifs en URLs absolues pour chaque signature
            if (result.data?.signatures && Array.isArray(result.data.signatures)) {
                result.data.signatures = result.data.signatures.map(sig => ({
                    ...sig,
                    grid_data: sig.grid_data ? convertUrlsToAbsolute(sig.grid_data) : sig.grid_data
                }));
            }

            return result;
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Créer une nouvelle signature
    async createSignature(data) {
        if (!API_CONFIG.enabled) return null;

        try {
            // Convertir les URLs absolues en chemins relatifs avant sauvegarde
            const dataToSave = {
                ...data,
                grid_data: data.grid_data ? convertUrlsToRelative(data.grid_data) : data.grid_data
            };

            const response = await fetch(`${API_CONFIG.baseUrl}/signatures`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(dataToSave)
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la création');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Mettre à jour une signature
    async updateSignature(id, data) {
        if (!API_CONFIG.enabled) return null;

        try {
            // Convertir les URLs absolues en chemins relatifs avant sauvegarde
            const dataToSave = {
                ...data,
                grid_data: data.grid_data ? convertUrlsToRelative(data.grid_data) : data.grid_data
            };

            const response = await fetch(`${API_CONFIG.baseUrl}/signatures/${id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(dataToSave)
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la mise à jour');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Supprimer une signature
    async deleteSignature(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/signatures/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la suppression');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Obtenir l'URL JSON d'une signature
    getSignatureUrl(uid) {
        return `${API_CONFIG.baseUrl}/signature/${uid}`;
    },

    // Obtenir l'URL HTML d'une signature
    getSignatureHtmlUrl(uid) {
        return `${API_CONFIG.baseUrl}/signature/html/${uid}`;
    },

    // Récupérer une signature complète (pour thumbnails)
    async getSignature(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/signatures/${id}`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Signature non trouvée');
            }
            const result = await response.json();

            // Convertir les chemins relatifs en URLs absolues
            if (result.data?.signature?.grid_data) {
                result.data.signature.grid_data = convertUrlsToAbsolute(result.data.signature.grid_data);
            }

            return result;
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Définir une signature par défaut
    async setDefaultSignature(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/signatures/${id}/set-default`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la mise à jour');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Activer/Désactiver une signature
    async toggleActiveSignature(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/signatures/${id}/toggle-active`, {
                method: 'PUT',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la mise à jour');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Supprimer plusieurs signatures en masse
    async bulkDeleteSignatures(ids) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/signatures/bulk-delete`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ ids })
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la suppression');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Dupliquer une signature
    async duplicateSignature(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/signatures/${id}/duplicate`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la duplication');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Vérifier le statut de l'API - CORRIGÉ avec gestion du slash
    async checkStatus() {
        if (!API_CONFIG.enabled) return 'disabled';

        try {
            // CORRECTION: Ajouter un slash final pour éviter la redirection 301
            const response = await fetch(`${API_CONFIG.baseUrl}/`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            });

            if (!response.ok) {
                console.error('API check failed:', response.status);
                return 'error';
            }

            const data = await response.json();
            console.log('API check response:', data);

            return data.status === 'ok' ? 'connected' : 'error';
        } catch (error) {
            console.error('API check error:', error);
            return 'error';
        }
    },

    // Upload thumbnail pour une signature
    async uploadThumbnail(signatureId, thumbnailBlob) {
        if (!API_CONFIG.enabled) return null;

        try {
            const formData = new FormData();
            formData.append('thumbnail', thumbnailBlob, 'thumbnail.png');

            const token = localStorage.getItem('access_token');
            const response = await fetch(`${API_CONFIG.baseUrl}/signatures/${signatureId}/thumbnail`, {
                method: 'POST',
                headers: {
                    ...(token && { 'Authorization': `Bearer ${token}` })
                    // Ne pas inclure Content-Type - le navigateur le définira automatiquement pour FormData
                },
                body: formData
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorText = await response.text();
                console.error('Upload thumbnail error response:', response.status, errorText);
                throw new Error(`Erreur lors de l'upload du thumbnail: ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Upload thumbnail pour un modèle
    async uploadTemplateThumbnail(templateId, thumbnailBlob) {
        if (!API_CONFIG.enabled) return null;

        try {
            const formData = new FormData();
            formData.append('thumbnail', thumbnailBlob, 'thumbnail.png');

            const token = localStorage.getItem('access_token');
            const response = await fetch(`${API_CONFIG.baseUrl}/templates/${templateId}/thumbnail`, {
                method: 'POST',
                headers: {
                    ...(token && { 'Authorization': `Bearer ${token}` })
                    // Ne pas inclure Content-Type - le navigateur le définira automatiquement pour FormData
                },
                body: formData
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorText = await response.text();
                console.error('Upload template thumbnail error response:', response.status, errorText);
                throw new Error(`Erreur lors de l'upload du thumbnail: ${response.status}`);
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Lister les versions d'une entité (signature ou template)
    async getVersions(entityType, entityId) {
        if (!API_CONFIG.enabled) return null;

        // Support de l'ancienne signature: getVersions(signatureId)
        if (typeof entityType === 'number' || (typeof entityType === 'string' && !entityId)) {
            entityId = entityType;
            entityType = 'signature';
        }

        const endpoint = entityType === 'template' ? 'templates' : 'signatures';

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/${endpoint}/${entityId}/versions`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors du chargement des versions');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Restaurer une version spécifique
    async restoreVersion(signatureId, versionId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/signatures/${signatureId}/versions/${versionId}/restore`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la restauration');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Dupliquer une signature à partir d'une version
    async duplicateFromVersion(signatureId, versionId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/signatures/${signatureId}/versions/${versionId}/duplicate`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la duplication');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Mettre à jour les paramètres d'une signature (dates, priorité, groupes AD)
    async updateSignatureSettings(id, settings) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/signatures/${id}/settings`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(settings)
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la mise à jour des paramètres');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Récupérer les assignations d'une signature (groupes et utilisateurs)
    async getSignatureAssignments(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/signatures/${id}/assignments`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des assignations');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // ============================================
    // TEMPLATES (modèles réutilisables)
    // ============================================

    // Lister tous les modèles accessibles
    async getTemplates(filters = {}) {
        if (!API_CONFIG.enabled) return null;

        try {
            const params = new URLSearchParams();
            if (filters.category) params.append('category', filters.category);
            if (filters.public !== undefined) params.append('public', filters.public);

            const url = `${API_CONFIG.baseUrl}/templates${params.toString() ? '?' + params.toString() : ''}`;
            const response = await fetch(url, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors du chargement des modèles');
            }
            const result = await response.json();

            // Convertir les chemins relatifs en URLs absolues pour chaque template
            if (result.data?.templates && Array.isArray(result.data.templates)) {
                result.data.templates = result.data.templates.map(tpl => ({
                    ...tpl,
                    grid_data: tpl.grid_data ? convertUrlsToAbsolute(tpl.grid_data) : tpl.grid_data
                }));
            }

            return result;
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Récupérer un modèle spécifique avec son grid_data
    async getTemplate(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/templates/${id}`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Modèle non trouvé');
            }
            const result = await response.json();

            // Convertir les chemins relatifs en URLs absolues
            if (result.data?.template?.grid_data) {
                result.data.template.grid_data = convertUrlsToAbsolute(result.data.template.grid_data);
            }

            return result;
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Créer un nouveau modèle
    async createTemplate(data) {
        if (!API_CONFIG.enabled) return null;

        try {
            // Convertir les URLs absolues en chemins relatifs avant sauvegarde
            const dataToSave = {
                ...data,
                grid_data: data.grid_data ? convertUrlsToRelative(data.grid_data) : data.grid_data
            };

            const response = await fetch(`${API_CONFIG.baseUrl}/templates`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(dataToSave)
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la création du modèle');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Mettre à jour un modèle
    async updateTemplate(id, data) {
        if (!API_CONFIG.enabled) return null;

        try {
            // Convertir les URLs absolues en chemins relatifs avant sauvegarde
            const dataToSave = {
                ...data,
                grid_data: data.grid_data ? convertUrlsToRelative(data.grid_data) : data.grid_data
            };

            const response = await fetch(`${API_CONFIG.baseUrl}/templates/${id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(dataToSave)
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la mise à jour du modèle');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Supprimer un modèle
    async deleteTemplate(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/templates/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                // Essayer de lire le message d'erreur de l'API
                let errorMessage = 'Erreur lors de la suppression du modèle';
                try {
                    const errorData = await response.json();
                    if (errorData.error) {
                        errorMessage = errorData.error;
                    }
                } catch (parseError) {
                    // Si on ne peut pas parser la réponse, utiliser le message générique
                }
                throw new Error(errorMessage);
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Dupliquer un modèle
    async duplicateTemplate(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/templates/${id}/duplicate`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la duplication du modèle');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Récupérer les utilisations d'un modèle
    async getTemplateUsages(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/templates/${id}/usages`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des utilisations');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Mise à jour en cascade d'un modèle vers toutes les signatures
    async cascadeUpdateTemplate(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/templates/${id}/cascade-update`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la mise à jour en cascade');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Exporter un template en JSON
    async exportTemplate(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/templates/${id}/export`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de l\'export du modèle');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Importer un template depuis JSON
    async importTemplate(templateData) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/templates/import`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ template_data: templateData })
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de l\'import du modèle');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Insérer un template dans une signature
    async insertTemplate(signatureId, data) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/signatures/${signatureId}/insert-template`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de l\'insertion du modèle');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Verrouiller/déverrouiller un template dans une signature
    async toggleTemplateLock(signatureId, data) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/signatures/${signatureId}/toggle-template-lock`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors du verrouillage du modèle');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // ============================================
    // TEMPLATE VERSIONS
    // ============================================

    // Lister les versions d'un template
    async getTemplateVersions(templateId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/templates/${templateId}/versions`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors du chargement des versions');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Récupérer une version spécifique d'un template
    async getTemplateVersion(templateId, versionId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/templates/${templateId}/versions/${versionId}`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors du chargement de la version');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Restaurer une version de template (avec cascade update automatique)
    async restoreTemplateVersion(templateId, versionId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/templates/${templateId}/versions/${versionId}/restore`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la restauration');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // ============================================
    // SCHEDULED CHANGES (Modifications planifiées)
    // ============================================

    // Lister les modifications planifiées
    async getScheduledChanges(filters = {}) {
        if (!API_CONFIG.enabled) return null;

        try {
            const params = new URLSearchParams();
            if (filters.entity_type) params.append('entity_type', filters.entity_type);
            if (filters.entity_id) params.append('entity_id', filters.entity_id);
            if (filters.status) params.append('status', filters.status);

            const url = `${API_CONFIG.baseUrl}/scheduled-changes${params.toString() ? '?' + params.toString() : ''}`;
            const response = await fetch(url, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors du chargement des modifications planifiées');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Créer une modification planifiée
    async createScheduledChange(data) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/scheduled-changes`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(data)
            });

            // Vérifier le content-type AVANT de parser
            const contentType = response.headers.get('content-type');

            if (!contentType || !contentType.includes('application/json')) {
                // C'est du HTML ou autre chose, pas du JSON
                const errorText = await response.text();
                console.error('Erreur HTML reçue:', errorText.substring(0, 1000));
                throw new Error('Erreur serveur (HTML). Consultez la console pour plus de détails.');
            }

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Erreur lors de la création de la modification planifiée');
            }

            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Récupérer une modification planifiée spécifique
    async getScheduledChange(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/scheduled-changes/${id}`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Modification planifiée non trouvée');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Mettre à jour une modification planifiée
    async updateScheduledChange(id, data) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/scheduled-changes/${id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Erreur lors de la mise à jour');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Annuler une modification planifiée
    async cancelScheduledChange(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/scheduled-changes/${id}/cancel`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Erreur lors de l\'annulation');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // Supprimer une modification planifiée
    async deleteScheduledChange(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/scheduled-changes/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Erreur lors de la suppression');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // ============================================
    // MEDIA LIBRARY (Bibliothèque de médias)
    // ============================================

    /**
     * Lister les médias de l'organisation
     * GET /media?category=logo
     */
    async getMediaLibrary(category = null) {
        if (!API_CONFIG.enabled) return null;

        try {
            const url = category
                ? `${API_CONFIG.baseUrl}/media?category=${category}`
                : `${API_CONFIG.baseUrl}/media`;

            const response = await fetch(url, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors du chargement des médias');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Upload un média
     * POST /media
     * @param {File} file - Fichier à uploader
     * @param {Object} metadata - Métadonnées (alt_text, category)
     */
    async uploadMedia(file, metadata = {}) {
        if (!API_CONFIG.enabled) return null;

        try {
            const formData = new FormData();
            formData.append('file', file);

            if (metadata.alt_text) {
                formData.append('alt_text', metadata.alt_text);
            }
            if (metadata.category) {
                formData.append('category', metadata.category);
            }

            const token = localStorage.getItem('access_token');
            const response = await fetch(`${API_CONFIG.baseUrl}/media`, {
                method: 'POST',
                headers: {
                    ...(token && { 'Authorization': `Bearer ${token}` })
                    // NE PAS inclure Content-Type pour FormData (le navigateur le fait automatiquement)
                },
                body: formData
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Erreur lors de l\'upload');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer un média spécifique
     * GET /media/:id
     */
    async getMedia(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/media/${id}`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Média non trouvé');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Mettre à jour les métadonnées d'un média
     * PUT /media/:id
     */
    async updateMediaMetadata(id, metadata) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/media/${id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(metadata)
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la mise à jour');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Supprimer un média
     * DELETE /media/:id
     */
    async deleteMedia(id) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/media/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                // Parse error message from response body
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la suppression');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // ============================================
    // ORGANISATIONS - MEMBRES
    // ============================================

    /**
     * Lister les membres d'une organisation
     * GET /organizations/:id/members
     */
    async getOrganizationMembers(organizationId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/organizations/${organizationId}/members`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des membres');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Modifier le rôle d'un membre
     * PUT /organizations/:organizationId/members/:memberId
     */
    async updateMemberRole(organizationId, memberId, roleId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/organizations/${organizationId}/members/${memberId}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ role_id: roleId })
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la modification du rôle');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Supprimer un membre
     * DELETE /organizations/:organizationId/members/:memberId
     */
    async removeMember(organizationId, memberId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/organizations/${organizationId}/members/${memberId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la suppression du membre');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // ============================================
    // ORGANISATIONS - INVITATIONS
    // ============================================

    /**
     * Inviter un membre
     * POST /organizations/:id/invite
     */
    async inviteMember(organizationId, email, roleId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/organizations/${organizationId}/invite`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ email, role_id: roleId })
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de l\'invitation');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Lister les invitations en attente
     * GET /organizations/:id/invitations
     */
    async getOrganizationInvitations(organizationId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/organizations/${organizationId}/invitations`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des invitations');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Révoquer une invitation
     * DELETE /organizations/:organizationId/invitations/:invitationId
     */
    async revokeInvitation(organizationId, invitationId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/organizations/${organizationId}/invitations/${invitationId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la révocation');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Accepter une invitation
     * POST /invitations/accept/:token
     */
    async acceptInvitation(token) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/invitations/accept/${token}`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de l\'acceptation');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer les rôles disponibles
     * Cette méthode pourrait appeler un endpoint dédié ou utiliser des constantes
     */
    getOrganizationRoles() {
        return [
            { id: 1, name: 'owner', label: 'Propriétaire' },
            { id: 2, name: 'admin', label: 'Administrateur' },
            { id: 3, name: 'editor', label: 'Éditeur' },
            { id: 4, name: 'viewer', label: 'Observateur' }
        ];
    },

    // ============================================
    // DIRECTORY API (LDAP/Azure AD Integration)
    // ============================================

    /**
     * Récupérer toutes les configurations d'annuaire
     * GET /directory/configs
     */
    async getDirectoryConfigs() {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/configs`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des configurations');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Créer une nouvelle configuration d'annuaire
     * POST /directory/configs
     */
    async createDirectoryConfig(config) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/configs`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(config)
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la création');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Mettre à jour une configuration d'annuaire
     * PUT /directory/configs/{id}
     */
    async updateDirectoryConfig(configId, config) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/configs/${configId}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(config)
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la mise à jour');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Supprimer une configuration d'annuaire
     * DELETE /directory/configs/{id}
     */
    async deleteDirectoryConfig(configId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/configs/${configId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la suppression');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Tester la connexion d'une configuration d'annuaire
     * POST /directory/configs/{id}/test-connection
     */
    async testDirectoryConnection(configId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/configs/${configId}/test-connection`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Échec de la connexion');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Lancer une synchronisation pour une configuration spécifique
     * POST /directory/configs/{id}/sync
     */
    async syncDirectory(configId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/configs/${configId}/sync`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur de synchronisation');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer les groupes synchronisés
     * GET /directory/groups?config_id=X (filtre optionnel)
     */
    async getDirectoryGroups(configId = null) {
        if (!API_CONFIG.enabled) return null;

        try {
            const params = configId ? `?config_id=${configId}` : '';
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/groups${params}`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des groupes');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer les employés synchronisés
     * GET /directory/users?config_id=X (filtre optionnel)
     */
    async getDirectoryUsers(configId = null) {
        if (!API_CONFIG.enabled) return null;

        try {
            const params = configId ? `?config_id=${configId}` : '';
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/users${params}`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des employés');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer les données live d'un utilisateur depuis l'AD
     * GET /directory/users/{id}/live-data
     */
    async getDirectoryUserLiveData(userId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/users/${userId}/live-data`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des données utilisateur');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer les logs de synchronisation
     * GET /directory/logs?config_id=X (filtre optionnel)
     */
    async getDirectoryLogs(limit = 20, offset = 0, configId = null) {
        if (!API_CONFIG.enabled) return null;

        try {
            let url = `${API_CONFIG.baseUrl}/directory/logs?limit=${limit}&offset=${offset}`;
            if (configId) url += `&config_id=${configId}`;

            const response = await fetch(url, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des logs');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Assigner une signature à un groupe
     * POST /directory/groups/:groupId/assign-signature
     */
    async assignSignatureToGroup(groupId, signatureId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/groups/${groupId}/assign-signature`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ signature_id: signatureId })
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de l\'assignation');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Retirer une signature d'un groupe
     * DELETE /directory/groups/:groupId/signatures/:signatureId
     */
    async removeSignatureFromGroup(groupId, signatureId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/groups/${groupId}/signatures/${signatureId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la suppression');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Mettre à jour la priorité d'un groupe
     * PUT /directory/groups/:groupId/priority
     */
    async updateGroupPriority(groupId, priority) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/groups/${groupId}/priority`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ priority })
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la mise à jour de la priorité');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Assigner une signature à un utilisateur individuel
     * POST /directory/users/:userId/assign-signature
     */
    async assignSignatureToUser(userId, signatureId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/users/${userId}/assign-signature`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ signature_id: signatureId })
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de l\'assignation');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Retirer une signature d'un utilisateur
     * DELETE /directory/users/:userId/signatures/:signatureId
     */
    async removeSignatureFromUser(userId, signatureId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/users/${userId}/signatures/${signatureId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la suppression');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer les signatures effectives d'un utilisateur selon l'algorithme de priorité
     * GET /user-signatures?email=xxx&org_id=xxx&date=xxx
     */
    async getUserEffectiveSignatures(email, orgId, date = null) {
        if (!API_CONFIG.enabled) return null;

        try {
            const params = new URLSearchParams({
                email,
                org_id: orgId
            });
            if (date) {
                params.append('date', date);
            }

            const response = await fetch(`${API_CONFIG.baseUrl}/user-signatures?${params.toString()}`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la récupération des signatures');
            }
            const result = await response.json();

            // Convertir les chemins relatifs en URLs absolues pour chaque signature
            if (result.data?.valid_signatures && Array.isArray(result.data.valid_signatures)) {
                result.data.valid_signatures = result.data.valid_signatures.map(sig => ({
                    ...sig,
                    grid_data: sig.grid_data ? convertUrlsToAbsolute(sig.grid_data) : sig.grid_data
                }));
            }

            return result;
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // ============================================
    // SHARED MAILBOXES (Boîtes mail partagées)
    // ============================================

    /**
     * Marquer/démarquer un utilisateur comme boîte partagée
     * PUT /directory/users/:userId/shared-mailbox
     */
    async toggleSharedMailbox(userId, isSharedMailbox) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/users/${userId}/shared-mailbox`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ is_shared_mailbox: isSharedMailbox })
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la mise à jour');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Mettre à jour la personnalisation d'un utilisateur (greeting, profile_photo)
     * PUT /directory/users/:userId/personalization
     */
    async updateUserPersonalization(userId, data) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/users/${userId}/personalization`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la mise à jour');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Uploader une photo de profil pour un utilisateur
     * POST /directory/users/:userId/profile-photo
     */
    async uploadUserProfilePhoto(userId, file) {
        if (!API_CONFIG.enabled) return null;

        try {
            const formData = new FormData();
            formData.append('photo', file);

            const token = localStorage.getItem('access_token');
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/users/${userId}/profile-photo`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                    // Ne pas mettre Content-Type, le navigateur le fait automatiquement pour FormData
                },
                body: formData
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de l\'upload de la photo');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer la liste des boîtes partagées
     * GET /directory/shared-mailboxes
     */
    async getSharedMailboxes() {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/shared-mailboxes`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des boîtes partagées');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer les opérateurs d'une boîte partagée
     * GET /directory/shared-mailboxes/:mailboxId/operators
     */
    async getMailboxOperators(mailboxId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/shared-mailboxes/${mailboxId}/operators`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des opérateurs');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Créer un opérateur
     * POST /directory/shared-mailboxes/:mailboxId/operators
     */
    async createOperator(mailboxId, operatorData) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/shared-mailboxes/${mailboxId}/operators`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(operatorData)
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la création de l\'opérateur');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Modifier un opérateur
     * PUT /directory/operators/:operatorId
     */
    async updateOperator(operatorId, operatorData) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/operators/${operatorId}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(operatorData)
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la mise à jour de l\'opérateur');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Supprimer un opérateur
     * DELETE /directory/operators/:operatorId
     */
    async deleteOperator(operatorId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/operators/${operatorId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la suppression de l\'opérateur');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Importer des opérateurs depuis un fichier CSV
     * POST /directory/shared-mailboxes/:mailboxId/operators/import
     */
    async importOperators(mailboxId, file) {
        if (!API_CONFIG.enabled) return null;

        try {
            const formData = new FormData();
            formData.append('file', file);

            const token = localStorage.getItem('access_token');
            const response = await fetch(`${API_CONFIG.baseUrl}/directory/shared-mailboxes/${mailboxId}/operators/import`, {
                method: 'POST',
                headers: {
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: formData
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de l\'import');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Telecharger le template d'import des operateurs
     * GET /directory/operators/template
     */
    getOperatorTemplateUrl() {
        return `${API_CONFIG.baseUrl}/directory/operators/template`;
    },

    // ============================================
    // API KEYS
    // ============================================

    /**
     * Lister les clés API de l'organisation
     * GET /api-keys
     */
    async getApiKeys() {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/api-keys`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la recuperation des clés API');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Creer une nouvelle cle API
     * POST /api-keys
     * ATTENTION: Le secret n'est retourne qu'une seule fois!
     */
    async createApiKey(name = 'Cle API') {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/api-keys`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ name })
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la creation de la cle API');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Revoquer une cle API
     * DELETE /api-keys/:id
     */
    async revokeApiKey(keyId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/api-keys/${keyId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la revocation de la cle API');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Modifier le nom d'une cle API
     * PUT /api-keys/:id
     */
    async updateApiKeyName(keyId, name) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/api-keys/${keyId}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ name })
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la modification de la cle API');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // ============================================
    // ORGANISATION INFO
    // ============================================

    /**
     * Recuperer les informations de l'organisation
     * GET /organizations/:id/info
     */
    async getOrganizationInfo(organizationId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/organizations/${organizationId}/info`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la recuperation des informations');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Mettre a jour les informations de l'organisation
     * PUT /organizations/:id/info
     */
    async updateOrganizationInfo(organizationId, data) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/organizations/${organizationId}/info`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la mise a jour');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Upload le logo de l'organisation
     * POST /organizations/:id/logo
     */
    async uploadOrganizationLogo(organizationId, file) {
        if (!API_CONFIG.enabled) return null;

        try {
            const formData = new FormData();
            formData.append('logo', file);

            const token = localStorage.getItem('access_token');
            const response = await fetch(`${API_CONFIG.baseUrl}/organizations/${organizationId}/logo`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de l\'upload du logo');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Supprimer le logo de l'organisation
     * DELETE /organizations/:id/logo
     */
    async deleteOrganizationLogo(organizationId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/organizations/${organizationId}/logo`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la suppression du logo');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // ============================================
    // STATISTIQUES
    // ============================================

    /**
     * Récupérer les statistiques globales de l'organisation
     * GET /statistics/overview
     * @param {string} period - Période (24h, 7d, 30d, 90d)
     */
    async getStatisticsOverview(period = '7d') {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/statistics/overview?period=${period}`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des statistiques');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer les top liens cliqués
     * GET /statistics/top-links
     * @param {string} period - Période (24h, 7d, 30d, 90d)
     * @param {number} limit - Nombre de liens à retourner
     */
    async getTopLinks(period = '7d', limit = 10) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/statistics/top-links?period=${period}&limit=${limit}`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des top liens');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer les statistiques par signature (liste)
     * GET /statistics/signatures
     * @param {string} period - Période (24h, 7d, 30d, 90d)
     */
    async getSignaturesStats(period = '7d') {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/statistics/signatures?period=${period}`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des statistiques signatures');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer les statistiques détaillées d'une signature
     * GET /statistics/signatures/:id
     * @param {number} signatureId - ID de la signature
     * @param {string} period - Période (24h, 7d, 30d, 90d)
     */
    async getSignatureStats(signatureId, period = '7d') {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/statistics/signatures/${signatureId}?period=${period}`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des statistiques');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // ==========================================
    // FACTURATION
    // ==========================================

    /**
     * Récupérer les informations de facturation
     * GET /invoices/billing-info
     */
    async getBillingInfo() {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/invoices/billing-info`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des informations de facturation');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Mettre à jour les informations de facturation
     * PUT /invoices/billing-info
     */
    async updateBillingInfo(data) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/invoices/billing-info`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la mise à jour des informations de facturation');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer l'usage courant
     * GET /invoices/current-usage
     */
    async getCurrentUsage() {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/invoices/current-usage`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération de l\'usage');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer les factures
     * GET /invoices
     */
    async getInvoices() {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/invoices`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des factures');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer les périodes de facturation
     * GET /invoices/billing-periods
     */
    async getBillingPeriods() {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/invoices/billing-periods`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des périodes');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer une facture
     * GET /invoices/:id
     */
    async getInvoice(invoiceId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/invoices/${invoiceId}`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération de la facture');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    // ============================================
    // PUBLIC TEMPLATES (Bibliothèque de modèles)
    // ============================================

    /**
     * Récupérer la liste des templates publics
     * GET /public-templates
     */
    async getPublicTemplates(category = null) {
        if (!API_CONFIG.enabled) return null;

        try {
            let url = `${API_CONFIG.baseUrl}/public-templates`;
            if (category) {
                url += `?category=${encodeURIComponent(category)}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération des modèles');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Récupérer un template public par son ID
     * GET /public-templates/:id
     */
    async getPublicTemplate(templateId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/public-templates/${templateId}`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de la récupération du modèle');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    },

    /**
     * Utiliser un template public (incrémente le compteur et retourne les données)
     * POST /public-templates/:id/use
     */
    async applyPublicTemplate(templateId) {
        if (!API_CONFIG.enabled) return null;

        try {
            const response = await fetch(`${API_CONFIG.baseUrl}/public-templates/${templateId}/use`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                handleAuthError(response);
                throw new Error('Erreur lors de l\'utilisation du modèle');
            }
            return await response.json();
        } catch (error) {
            console.error('API Error:', error);
            throw error;
        }
    }
};

export { API, API_CONFIG };
